import { useEffect, useState } from 'react';
import { api } from '../api.ts';
import type {
  CategoryBreakdown,
  DifficultyBreakdown,
  UserStats,
} from '../../../shared/types.ts';

interface Props {
  /** Pre-loaded stats for the current viewer; ignored when `username` is set. */
  baseStats?: UserStats | null;
  /** When provided, render the public stats page for that registered user instead of "you". */
  username?: string;
  /** Back-button handler shown only on the public per-user view. */
  onBack?: () => void;
}

export function StatsView({ baseStats, username, onBack }: Props): React.ReactElement {
  const isOther = !!username;
  const [overall, setOverall] = useState<UserStats | null>(isOther ? null : baseStats ?? null);
  const [byCategory, setByCategory] = useState<CategoryBreakdown[]>([]);
  const [byDifficulty, setByDifficulty] = useState<DifficultyBreakdown[]>([]);
  const [profile, setProfile] = useState<{
    username: string;
    displayName: string;
    avatarColor: string;
    createdAt: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [global, setGlobal] = useState<{
    totalRounds: number;
    totalAnswers: number;
    totalCorrect: number;
    players: number;
    globalAccuracyPct: number;
  } | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setActiveCategory(null);
    if (username) {
      setOverall(null);
      setByCategory([]);
      setByDifficulty([]);
      setProfile(null);
      void api
        .userStats(username)
        .then((res) => {
          setOverall(res.overall);
          setByCategory(res.byCategory);
          setByDifficulty(res.byDifficulty);
          setProfile({
            username: res.user.username,
            displayName: res.user.displayName,
            avatarColor: res.user.avatarColor,
            createdAt: res.user.createdAt,
          });
        })
        .catch((e: Error) => setError(e.message ?? 'Failed to load.'));
    } else {
      setProfile(null);
      void api.myStats().then((res) => {
        setOverall(res.overall);
        setByCategory(res.byCategory);
        setByDifficulty(res.byDifficulty);
      });
    }
    void api.globalStats().then(setGlobal);
  }, [username]);

  const filteredDiff = activeCategory ? null : byDifficulty;

  if (isOther && error) {
    return (
      <div className="stats-page">
        {onBack && (
          <button className="ghost small" onClick={onBack}>← Back to ranking</button>
        )}
        <h2>{username}</h2>
        <p className="muted">{error}</p>
      </div>
    );
  }

  return (
    <div className="stats-page">
      {isOther && onBack && (
        <button className="ghost small" onClick={onBack}>← Back to ranking</button>
      )}
      {isOther ? (
        <div className="row gap-sm" style={{ alignItems: 'center' }}>
          {profile && (
            <span
              className="lb-avatar"
              style={{ background: profile.avatarColor, width: 36, height: 36, fontSize: 16 }}
            >
              {profile.displayName.slice(0, 1).toUpperCase()}
            </span>
          )}
          <h2 style={{ margin: 0 }}>{profile?.username ?? username}</h2>
        </div>
      ) : (
        <h2>Your stats</h2>
      )}

      <div className="stat-grid">
        <Card label="Score" value={fmt(overall?.totalScore)} accent="brand" />
        <Card label="Accuracy" value={overall ? `${overall.accuracyPct}%` : '—'} />
        <Card label="Correct" value={fmt(overall?.correctCount)} accent="good" />
        <Card label="Wrong" value={fmt(overall?.wrongCount)} accent="bad" />
        <Card label="Skipped" value={fmt(overall?.skippedCount)} />
        <Card label="Best streak" value={fmt(overall?.bestStreak)} accent="streak" />
        <Card label="Current streak" value={fmt(overall?.currentStreak)} />
        <Card
          label="Rank"
          value={overall?.rank ? `#${overall.rank}` : '—'}
          sub={overall?.totalRanked ? `of ${overall.totalRanked}` : ''}
        />
      </div>

      <h3>By category</h3>
      <div className="cat-grid">
        {byCategory.map((c) => {
          const isActive = activeCategory === c.category.slug;
          return (
            <button
              key={c.category.id}
              className={`cat-tile ${isActive ? 'active' : ''}`}
              onClick={() => setActiveCategory(isActive ? null : c.category.slug)}
              disabled={c.questionsSeen === 0}
            >
              <div className="cat-emoji">{c.category.emoji}</div>
              <div className="cat-name">{c.category.name}</div>
              <div className="cat-stats">
                <span className="cat-score">{c.totalScore}</span>
                <span className="muted small">
                  {c.questionsSeen > 0 ? `${c.accuracyPct}% acc · ${c.questionsSeen} seen` : 'no questions yet'}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <h3>By difficulty {activeCategory ? `· ${byCategory.find((c) => c.category.slug === activeCategory)?.category.name ?? ''}` : ''}</h3>
      <div className="diff-rows">
        {(filteredDiff ?? []).map((d) => (
          <div key={d.difficulty.id} className={`diff-row diff-${d.difficulty.slug}`}>
            <span className="diff-name">{d.difficulty.name}</span>
            <div className="diff-bar">
              <div
                className="diff-bar-fill"
                style={{ width: `${Math.max(2, Math.min(100, d.accuracyPct))}%` }}
              />
            </div>
            <span className="diff-pct">{d.questionsSeen > 0 ? `${d.accuracyPct}%` : '—'}</span>
            <span className="muted small">{d.questionsSeen} seen</span>
            <span className={`small ${d.totalScore >= 0 ? 'good' : 'bad'}`}>
              {d.totalScore >= 0 ? `+${d.totalScore}` : d.totalScore}
            </span>
          </div>
        ))}
        {activeCategory && (
          <p className="muted small">Per-difficulty breakdowns inside a single category aren’t tracked separately — pick "All categories" above to see your difficulty performance.</p>
        )}
      </div>

      {global && (
        <>
          <h3>Globally</h3>
          <div className="stat-grid">
            <Card label="Rounds run" value={fmt(global.totalRounds)} />
            <Card label="Players" value={fmt(global.players)} />
            <Card label="Answers" value={fmt(global.totalAnswers)} />
            <Card label="Global accuracy" value={`${global.globalAccuracyPct}%`} />
          </div>
        </>
      )}
    </div>
  );
}

function fmt(n: number | undefined | null): string {
  if (n === undefined || n === null) return '—';
  return n.toLocaleString();
}

function Card({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: 'good' | 'bad' | 'brand' | 'streak' }): React.ReactElement {
  return (
    <div className={`stat-card ${accent ? `accent-${accent}` : ''}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}
